import React, { useEffect, useMemo, useState } from "react";
import { Wrench, Search, ExternalLink, Loader2, BookOpen, X } from "lucide-react";
import { useSearchParams, Link } from "react-router-dom";
import { getCrossLinks } from "@/lib/crossLinks";

export default function DashboardToolbox() {
  const [searchParams, setSearchParams] = useSearchParams();
  const via = searchParams.get("via") || "";

  const [data, setData] = useState(null); // { playbooks, tools, toolToPlaybooks, playbookToTools }
  const [error, setError] = useState(null);
  const [q, setQ] = useState("");
  const [kind, setKind] = useState("");
  const [cat, setCat] = useState("");

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const d = await getCrossLinks();
        if (cancel) return;
        if (!d.tools.length) {
          setError("Couldn't load the toolbox feed. Try again in a moment.");
          setData({ ...d });
        } else {
          setData(d);
        }
      } catch {
        if (!cancel) setError("Couldn't load the toolbox feed.");
      }
    })();
    return () => {
      cancel = true;
    };
  }, []);

  const tools = data?.tools || [];
  const playbookToTools = data?.playbookToTools;
  const toolToPlaybooks = data?.toolToPlaybooks;

  const viaPlaybook = useMemo(
    () => (via && data ? data.playbooks.find((p) => p.slug === via) || null : null),
    [via, data]
  );

  const kinds = useMemo(() => {
    if (!tools.length) return [""];
    const s = new Set(tools.map((t) => t.type || t.kind).filter(Boolean));
    return ["", ...Array.from(s).sort()];
  }, [tools]);

  const cats = useMemo(() => {
    if (!tools.length) return [""];
    const s = new Set(tools.map((t) => t.category).filter(Boolean));
    return ["", ...Array.from(s).sort()];
  }, [tools]);

  const viaSet = useMemo(() => {
    if (!via || !playbookToTools) return null;
    return new Set((playbookToTools.get(via) || []).map((x) => x.toolIdx));
  }, [via, playbookToTools]);

  const list = useMemo(() => {
    if (!tools.length) return [];
    return tools
      .map((t, i) => ({ t, i }))
      .filter(({ t, i }) => {
        if (viaSet && !viaSet.has(i)) return false;
        const k = t.type || t.kind || "";
        if (kind && k !== kind) return false;
        if (cat && t.category !== cat) return false;
        if (!q) return true;
        const hay = `${t.name || ""} ${t.one_liner || t.blurb || ""} ${t.category || ""}`.toLowerCase();
        return hay.includes(q.toLowerCase());
      });
  }, [tools, viaSet, kind, cat, q]);

  const total = tools.length || null;

  const clearVia = () =>
    setSearchParams((p) => {
      p.delete("via");
      return p;
    });

  return (
    <div data-testid="dash-toolbox" className="space-y-6">
      <div>
        <h1 className="font-display text-3xl md:text-4xl flex items-center gap-3">
          <Wrench className="text-[#E10600]" /> TOOLBOX
        </h1>
        <p className="font-mono-body text-sm text-[#0a0a0a]/65 mt-1.5 max-w-2xl">
          {total != null ? (
            <>
              <b data-testid="tb-total">{total}</b> hand-picked tools, prompts and repos — each one
              linked back to the playbook that uses it.
            </>
          ) : (
            "Loading the live toolbox feed…"
          )}
        </p>
      </div>

      {viaPlaybook && (
        <div
          data-testid="tb-via-banner"
          className="bg-[#0a0a0a] text-[#FDFBF7] border-4 border-[#0a0a0a] shadow-brutal p-4 flex items-center justify-between gap-3 flex-wrap"
        >
          <div className="flex items-center gap-3">
            <BookOpen size={18} strokeWidth={2.5} className="text-[#FFC400] shrink-0" />
            <div>
              <div className="font-accent text-[10px] text-[#FFC400]">FILTERED BY PLAYBOOK</div>
              <div className="font-display text-base md:text-lg leading-tight">{viaPlaybook.title}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a
              href={viaPlaybook.url}
              target="_blank"
              rel="noopener noreferrer"
              className="hover-pop bg-[#FFC400] text-[#0a0a0a] border-2 border-[#FFC400] px-3 py-2 font-accent text-[11px] inline-flex items-center gap-1.5"
            >
              Open playbook <ExternalLink size={11} strokeWidth={3} />
            </a>
            <button
              onClick={clearVia}
              data-testid="tb-via-clear"
              className="hover-pop bg-[#E10600] text-white border-2 border-[#E10600] px-3 py-2 font-accent text-[11px] inline-flex items-center gap-1.5"
            >
              <X size={11} strokeWidth={3} /> Clear filter
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-12 gap-3">
        <div className="col-span-12 md:col-span-7 relative">
          <Search
            size={16}
            strokeWidth={2.5}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0a0a0a]/55"
          />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={`Search ${total ?? ""} tools…`.trim()}
            data-testid="tb-search"
            className="w-full bg-white border-2 border-[#0a0a0a] pl-10 pr-4 py-3 font-mono-body text-[13px] shadow-brutal-sm focus:outline-none focus:bg-[#FDFBF7]"
          />
        </div>
        <div className="col-span-6 md:col-span-2 flex flex-wrap gap-2">
          {kinds.slice(0, 6).map((k) => (
            <button
              key={k || "all"}
              onClick={() => setKind(k)}
              data-testid={`tb-kind-${k || "all"}`}
              className={`font-accent text-[10px] border-2 border-[#0a0a0a] px-2 py-1.5 ${
                k === kind ? "bg-[#E10600] text-white" : "bg-white"
              }`}
            >
              {k ? k.toUpperCase() : "ALL"}
            </button>
          ))}
        </div>
        <div className="col-span-6 md:col-span-3">
          <select
            value={cat}
            onChange={(e) => setCat(e.target.value)}
            data-testid="tb-cat"
            className="w-full bg-white border-2 border-[#0a0a0a] px-3 py-2.5 font-mono-body text-[12px] shadow-brutal-sm"
          >
            {cats.map((c) => (
              <option key={c || "all"} value={c}>
                {c || "All categories"}
              </option>
            ))}
          </select>
        </div>
      </div>

      {data === null && (
        <div className="bg-white border-4 border-[#0a0a0a] shadow-brutal p-10 text-center font-mono-body text-sm text-[#0a0a0a]/65">
          <Loader2 className="mx-auto animate-spin mb-3" /> Pulling the live toolbox…
        </div>
      )}

      {error && (
        <div className="bg-white border-4 border-[#0a0a0a] shadow-brutal p-6 font-mono-body text-sm text-[#E10600]">
          {error}
        </div>
      )}

      {data && tools.length > 0 && (
        <>
          <div className="font-mono-body text-[12px] text-[#0a0a0a]/65">
            Showing <b className="text-[#0a0a0a]">{list.length}</b> of {total}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {list.map(({ t, i }) => {
              const slug = (t.name || `item-${i}`)
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, "-");
              const usedIn = toolToPlaybooks?.get(i) || [];
              const safeHost = (u) => {
                try { return new URL(u).hostname; } catch { return null; }
              };
              const host = t.url ? safeHost(t.url) : null;
              const logoUrl = t.logo || (host
                ? `https://www.google.com/s2/favicons?domain=${encodeURIComponent(host)}&sz=128`
                : null);
              return (
                <div
                  key={`${slug}-${i}`}
                  data-testid={`tb-item-${slug}`}
                  className="bg-white border-2 border-[#0a0a0a] shadow-brutal-sm p-4 flex flex-col"
                >
                  <a
                    href={t.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover-pop -m-4 mb-0 p-4 block"
                  >
                    <div className="flex items-center justify-between mb-2 gap-2">
                      <span className="font-accent text-[10px] text-[#E10600] uppercase">
                        {t.type || t.kind || "tool"}
                      </span>
                      <span className="font-mono-body text-[10px] text-[#0a0a0a]/55 truncate text-right">
                        {t.category}
                      </span>
                    </div>
                    <div className="flex items-start gap-3">
                      {logoUrl ? (
                        <img
                          src={logoUrl}
                          alt=""
                          loading="lazy"
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                          }}
                          className="w-9 h-9 border-2 border-[#0a0a0a] bg-white object-contain shrink-0"
                        />
                      ) : (
                        <div className="w-9 h-9 border-2 border-[#0a0a0a] bg-[#FDFBF7] flex items-center justify-center font-display text-[10px] text-[#0a0a0a]/70 shrink-0">
                          {(t.name || "?").slice(0, 2).toUpperCase()}
                        </div>
                      )}
                      <div className="min-w-0">
                        <div className="font-display text-base leading-tight">{t.name}</div>
                        {(t.one_liner || t.blurb) && (
                          <p className="font-mono-body text-[11px] text-[#0a0a0a]/70 mt-1">
                            {t.one_liner || t.blurb}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="mt-3 inline-flex items-center gap-1.5 font-accent text-[11px]">
                      Open <ExternalLink size={10} strokeWidth={3} />
                    </div>
                  </a>
                  {usedIn.length > 0 && (
                    <div className="mt-3 pt-3 border-t-2 border-[#0a0a0a]/10">
                      <div className="font-accent text-[9px] text-[#0a0a0a]/60 mb-1.5 flex items-center gap-1">
                        <BookOpen size={9} strokeWidth={3} /> USED IN
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {usedIn.slice(0, 3).map((pb) => (
                          <Link
                            key={pb.slug}
                            to={`/dashboard/playbooks?focus=${pb.slug}`}
                            data-testid={`tb-link-pb-${pb.slug}`}
                            className="font-mono-body text-[10px] bg-[#FFC400] border-2 border-[#0a0a0a] px-1.5 py-0.5 hover:bg-[#0a0a0a] hover:text-[#FFC400]"
                            title={pb.title}
                          >
                            {pb.title.length > 36 ? pb.title.slice(0, 34) + "…" : pb.title}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
            {list.length === 0 && (
              <div className="col-span-full font-mono-body text-sm text-[#0a0a0a]/65 text-center py-10">
                Nothing matches that search.
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
